/* eslint-disable eslint-comments/no-restricted-disable */
import type { PreloadFetchPolicy, EntryPoint, EntryPointComponent } from 'react-relay';
// eslint-disable-next-line @atlassian/react-entrypoint/no-imports-in-entrypoint-file -- pure, dependency-free helpers from a tiny leaf package
import { getAttachmentDateFilterRange } from '@jira/business__attachments-filters/src/attachmentDateFilters.tsx';
// eslint-disable-next-line @atlassian/react-entrypoint/no-imports-in-entrypoint-file -- pure, dependency-free helpers from a tiny leaf package
import { toMimeTypes } from '@jira/business__attachments-filters/src/attachmentFileTypes.tsx';
// eslint-disable-next-line @atlassian/react-entrypoint/no-imports-in-entrypoint-file -- pure, dependency-free helpers from a tiny leaf package
import {
	parseAddedByParam,
	parseDateAddedParam,
	parseFileTypesParam,
} from '@jira/business__attachments-filters/src/attachmentFilterUrlParams.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from './__generated__/JiraBusinessAttachmentsAppMemoQuery$parameters.ts';
import type { JiraBusinessAttachmentsAppMemoQuery } from './__generated__/JiraBusinessAttachmentsAppMemoQuery.graphql.ts';

type AttachmentsEntryPointComponent = EntryPointComponent<
	{
		jwmAttachmentsQuery: JiraBusinessAttachmentsAppMemoQuery;
	},
	{},
	{},
	{}
>;

export const attachmentsEntryPoint: EntryPoint<
	AttachmentsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<AttachmentsEntryPointComponent>(
		/* webpackChunkName: "async-graph" */ './JiraBusinessAttachmentsAppMemo.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => {
		// This entry point should only load in this route
		// once overview is supported by backend, we should have a different entry point for it
		const { projectKey } = context.match.params;
		if (!projectKey) {
			throw new Error('projectKey is required');
		}

		const authorIds = expVal('attachment_persist_filters_url', 'isEnabled', false)
			? parseAddedByParam(context.query?.addedBy)
			: [];
		const mimeTypes = expVal('attachment_persist_filters_url', 'isEnabled', false)
			? toMimeTypes(parseFileTypesParam(context.query?.fileTypes))
			: [];
		const dateAdded = expVal('attachment_persist_filters_url', 'isEnabled', false)
			? parseDateAddedParam(context.query?.dateAdded)
			: null;
		const range = dateAdded ? getAttachmentDateFilterRange(dateAdded) : null;
		const dateRange = range
			? {
					after: range.after.toISOString(),
					before: range.before.toISOString(),
				}
			: {
					before: undefined,
					after: undefined,
				};

		return {
			queries: {
				jwmAttachmentsQuery: {
					parameters,
					variables: {
						cloudId: tenantContext.cloudId,
						projectKey,
						projectKeys: [projectKey],
						filters: {
							fileName: '',
							authorIds,
							mimeTypes,
							dateRange,
						},
						durationInSeconds: 900,
						maxTokenLength: 65534,
						attachmentCount: 100,
						includeProjectTypeForSubProduct: fg('enable_attachments_in_jsw_nav'),
					},
					options: {
						// oxlint-disable-next-line typescript/consistent-type-assertions
						fetchPolicy: 'network-only' as PreloadFetchPolicy,
					},
				},
			},
			entryPoints: {},
			extraProps: {},
		};
	},
});
